"use client";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "../../../components/ui/dropdown-menu";
import { MoreVertical, FileDown, Edit, Info, Ban } from "lucide-react";
import { Order } from "../types/order";
import axios from "axios";
import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../config/api";
import { AuthMediaImg } from "../../../components/AuthMediaImg";
import { toMediaSrc } from "../../../utils/mediaSrc";
import { QuotationTemplate } from "./QuotationTemplate";
import { QuotationTemplate as QuotationTemplatePrint } from "./QuotationTemplate_ForPrint";
import { downloadPDF } from "../../../utils/pdfGenerator";
import { formatIndiaDateTime, formatIndiaDate, getToBeDeliveredFieldLabel } from "../../../utils/dateFormat";

const formatIndiaTime = formatIndiaDateTime;
const formatDateOnly = formatIndiaDate;

const findColorMeta = (value: any, colorsList: any[]) => {
  if (value == null || value === "") return null;
  const name =
    typeof value === "string" || typeof value === "number"
      ? String(value)
      : String(value.name || value.Name || "");
  const idFromValue =
    typeof value === "object" && value !== null
      ? value.id ?? value.colorId ?? value.color_id
      : undefined;
  const inlineImage =
    typeof value === "object" && value !== null
      ? value.image || value.colorImage || value.color_image
      : "";
  const matched = (colorsList || []).find((c) => {
    if (idFromValue != null && String(c?.id) === String(idFromValue)) return true;
    const n = String(c?.name || "")
      .trim()
      .toLowerCase();
    return n && n === name.trim().toLowerCase();
  });
  return {
    name: name || matched?.name || "Color",
    id: idFromValue ?? matched?.id,
    image: toMediaSrc(inlineImage) || "",
    hasImage: Boolean(
      toMediaSrc(inlineImage) || matched?.hasImage || matched?.colorImage || idFromValue || matched?.id
    ),
  };
};

const isColorSpecKey = (key: string, value: any) => {
  if (/color/i.test(String(key || ""))) return true;
  if (value && typeof value === "object") {
    return Boolean(value.image || value.colorImage || value.id || value.name);
  }
  return false;
};
interface Props {
  order: Order;
  refresh: () => void;
}

const getDateInputValue = (dateString: string | undefined) => {
  const match = dateString?.match(/^(\d{4}-\d{2}-\d{2})/);
  return match?.[1] || "";
};

export const OrderCard: React.FC<Props> = ({ order, refresh }) => {
  const [showOrderDetails,setShowOrderDetails]=useState<Order | null>(null)
  const [previewColor, setPreviewColor] = useState<any>(null);
  const [previewProduct, setPreviewProduct] = useState<any>(null);
  const [colorsList, setColorsList] = useState<any[]>([]);
  const [showDateModal, setShowDateModal] = useState(false);
  const [invoiceDate, setInvoiceDate] = useState(() => getDateInputValue(order.invoiceDate));
  const [dateLoading, setDateLoading] = useState(false);
  const [dateError, setDateError] = useState("");
  const [cancelLoading, setCancelLoading] = useState(false);
  const { authToken = "", id = "", role = "" } = useSelector(
    (state: any) => state.loginData || {}
  );

  useEffect(() => {
    if (!showOrderDetails || !id || !authToken) return;
    let cancelled = false;
    (async () => {
      try {
        const { fetchColorsCached } = await import("../../../utils/colorsCache");
        const { colors } = await fetchColorsCached({
          accountId: id,
          authToken,
          lite: true,
          maxResults: "all",
        });
        if (!cancelled) setColorsList(Array.isArray(colors) ? colors : []);
      } catch {
        if (!cancelled) setColorsList([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [showOrderDetails, id, authToken]);
const handleDownloadPDF = async () => {
  try {
    const invoiceNo = String(order.invoiceId || order.id || "invoice").trim();
    let fullOrder = order;
    try {
      const res = await axios.get(`${API_BASE_URL}account/${id}/order/${order.id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.data) fullOrder = res.data;
    } catch {
      /* use list order */
    }
    await downloadPDF(
      <QuotationTemplate order={fullOrder} />,
      `invoice-${invoiceNo}.pdf`
    );
  } catch (error) {
    console.error("Error downloading PDF:", error);
    alert("Failed to download PDF");
  }
};

const handleDownloadPrintFormatPDF = async () => {
  try {
    const invoiceNo = String(order.invoiceId || order.id || "invoice").trim();
    let fullOrder = order;
    try {
      const res = await axios.get(`${API_BASE_URL}account/${id}/order/${order.id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.data) fullOrder = res.data;
    } catch {
      /* use list order */
    }
    await downloadPDF(
      <QuotationTemplatePrint order={fullOrder} />,
      `invoice-${invoiceNo}-print.pdf`
    );
  } catch (error) {
    console.error("Error downloading Print Format PDF:", error);
    alert("Failed to download Print Format PDF");
  }
};
  const handleInvoiceDateSave = async () => {
    if (!invoiceDate) {
      setDateError("Please select an Invoice Date.");
      return;
    }

    try {
      setDateLoading(true);
      setDateError("");
      await axios.patch(
        `${API_BASE_URL}account/${id}/orders/${order.id}/invoice-date`,
        { invoiceDate },
        {
          headers: { Authorization: `Bearer ${authToken}` },
        }
      );
      setShowDateModal(false);
      refresh();
    } catch (e) {
      console.error("Error updating Invoice Date:", e);
      setDateError(
        axios.isAxiosError(e)
          ? e.response?.data?.message || "Unable to update the Invoice Date."
          : "Unable to update the Invoice Date."
      );
    } finally {
      setDateLoading(false);
    }
  };

  const handleCancelInvoice = async () => {
    if (
      !window.confirm(
        "Cancel this invoice? The order stays on Orders with Edit enabled again. No product or price data will be changed."
      )
    ) {
      return;
    }
    try {
      setCancelLoading(true);
      try {
        await axios.post(
          `${API_BASE_URL}account/${id}/orders/${order.id}/cancel-invoice`,
          {},
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
      } catch (primaryErr) {
        const status = axios.isAxiosError(primaryErr) ? primaryErr.response?.status : 0;
        if (status !== 404 && status !== 405) throw primaryErr;

        // Fallback: cancel-only path on order update (backend maps this to CancelInvoice)
        await axios.put(
          `${API_BASE_URL}account/${id}/order/update`,
          {
            id: order.id,
            status: order.status || "new",
            customerId: order.customerId,
            customerName: order.customerName,
            customerRole: order.customerRole || "",
            products: order.products || [],
            price: String(order.price ?? "0"),
            total: String(order.total ?? "0"),
            createdBy: order.createdBy || "",
            invoiceGenerated: false,
          },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
      }
      refresh();
    } catch (e) {
      console.error("Error cancelling invoice:", e);
      const msg = axios.isAxiosError(e)
        ? e.response?.data?.message ||
          (e.response?.status === 404
            ? "Cancel invoice API not found. Restart the backend server and try again."
            : "Unable to cancel the invoice.")
        : "Unable to cancel the invoice.";
      alert(msg);
    } finally {
      setCancelLoading(false);
    }
  };
  

  return (
    <Card className="p-0 border-2 border-gray-200 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl hover:border-blue-300 transition-all group">
      <CardHeader className="bg-gradient-to-r from-orange-50 to-orange-100 flex justify-between items-start px-2.5 py-2 border-b-2 border-gray-200">
        <div className="flex-1 min-w-0">
          <CardTitle className="text-base font-bold text-gray-900 group-hover:text-orange-700 transition-colors truncate">
            {order.customerName}
          </CardTitle>
          <p className="text-[11px] text-gray-500 mt-0.5 font-medium">
            Invoice ID: <span className="text-orange-600 font-semibold">{order.invoiceId}</span>
          </p>
        </div>
        <div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="hover:bg-white">
                <MoreVertical className="w-4 h-4 text-gray-600" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-white rounded-lg shadow-lg">
              <DropdownMenuItem
                onClick={() => handleDownloadPrintFormatPDF()}
                className="cursor-pointer hover:bg-green-50 text-gray-700"
              >
                Download Print Format
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>

      <CardContent className="px-2.5 py-2 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
            <p className="text-[10px] text-gray-500 font-semibold mb-0.5">Created By</p>
            <p className="text-xs font-bold text-gray-900">{order.createdBy || "N/A"}</p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
            <p className="text-[10px] text-gray-500 font-semibold mb-0.5">Invoice Date</p>
            <p className="text-xs font-bold text-gray-900">
              {formatDateOnly(order.invoiceDate)}
            </p>
          </div>
        </div>

        <div className="bg-orange-50 border border-orange-200 rounded-lg p-2">
          <p className="text-[10px] text-gray-500 font-semibold mb-0.5">Total Amount</p>
          <p className="text-sm font-bold text-orange-700">₹{order.total ? parseFloat(order.total.toString()).toFixed(2) : "0.00"}</p>
        </div>

        <div className="flex gap-1.5 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={async () => {
              try {
                const res = await axios.get(`${API_BASE_URL}account/${id}/order/${order.id}`, {
                  headers: { Authorization: `Bearer ${authToken}` },
                });
                setShowOrderDetails((res.data || order) as Order);
              } catch {
                setShowOrderDetails(order);
              }
            }}
            className="flex-1 md:flex-none border-blue-300 text-blue-600 hover:bg-blue-50 font-semibold text-xs py-1"
          >
            <Info className="w-3.5 h-3.5 mr-1" /> View Details
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleDownloadPDF}
            className="flex-1 md:flex-none border-green-300 text-green-600 hover:bg-green-50 font-semibold text-xs py-1"
          >
            <FileDown className="w-3.5 h-3.5 mr-1" /> Download PDF
          </Button>
          {(role === "admin" || role === "employee") && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setInvoiceDate(getDateInputValue(order.invoiceDate));
                setDateError("");
                setShowDateModal(true);
              }}
              className="flex-1 md:flex-none border-orange-300 text-orange-700 hover:bg-orange-50 font-semibold text-xs py-1"
            >
              <Edit className="w-3.5 h-3.5 mr-1" /> Edit Invoice Date
            </Button>
          )}
          {(role === "admin" || role === "employee") && (
            <Button
              variant="outline"
              size="sm"
              disabled={cancelLoading}
              onClick={handleCancelInvoice}
              className="flex-1 md:flex-none border-red-300 text-red-700 hover:bg-red-50 font-semibold text-xs py-1"
            >
              <Ban className="w-3.5 h-3.5 mr-1" />
              {cancelLoading ? "Cancelling..." : "Cancel Invoice"}
            </Button>
          )}
        </div>
      </CardContent>

      {/* Invoice Details Drawer — use full order from showOrderDetails (not lite list item) */}
      {showOrderDetails && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setShowOrderDetails(null)} />
      )}
      <div
        className={`fixed md:left-1/2 md:top-1/2 md:transform md:-translate-x-1/2 md:-translate-y-1/2 bottom-0 left-0 right-0 md:bottom-auto bg-white z-50 transform transition-all duration-300 w-full md:w-[500px] md:h-auto max-h-screen md:max-h-[95vh] overflow-y-auto rounded-t-2xl md:rounded-lg ${
          showOrderDetails ? "translate-y-0 md:scale-100" : "translate-y-full md:scale-0 md:opacity-0 pointer-events-none"
        }`}
      >
        {showOrderDetails && (() => {
          const details = showOrderDetails;
          const showMoney = !!details.quotation;
          return (
        <>
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white flex justify-between items-center rounded-t-2xl md:rounded-t-lg">
          <h2 className="text-xl font-bold text-gray-900">
            Order Details - {details.customerName}
          </h2>
          <button
            onClick={() => setShowOrderDetails(null)}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            ✕
          </button>
        </div>

        <div className="p-6 pb-20 md:pb-6 space-y-4">

          {/* Order Details in Single Box */}
          <div className="bg-white border-2 border-gray-300 rounded-xl p-6 shadow-sm">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">Order Number</p>
                <p className="text-base font-bold text-gray-900 break-words">{details.id}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">Order Date & Time</p>
                <p className="text-base font-bold text-gray-900 break-words">{formatIndiaTime(details.createdAt)}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">Customer Name</p>
                <p className="text-base font-bold text-gray-900 break-words">{details.customerName}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">Customer ID</p>
                <p className="text-base font-bold text-gray-900 break-words">{details.customerId}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">Created On</p>
                <p className="text-base font-bold text-gray-900 break-words">{formatIndiaTime(details.createdAt)}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">Updated Date</p>
                <p className="text-base font-bold text-gray-900 break-words">{formatIndiaTime(details.updatedAt)}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">Delivery Date</p>
                <p className="text-base font-bold text-gray-900 break-words">{formatDateOnly(details.deliveryDate)}</p>
              </div>
              <div>
                <p className="text-xs font-bold text-gray-600 uppercase mb-1">{getToBeDeliveredFieldLabel(details.customerRole)}</p>
                <p className="text-base font-bold text-gray-900 break-words">{formatDateOnly(details.toBeDelivered)}</p>
              </div>
            </div>
          </div>

          {/* Price Summary — only when Create Quotation is enabled */}
       {showMoney && (
  <div className="bg-white border-2 border-blue-300 rounded-xl overflow-x-auto">
    <div className="w-full">
      {(() => {
        const hasFixing = parseFloat(details.fixing || "0") > 0;
        const hasTransport = parseFloat(details.transport || "0") > 0;
        const hasDiscount = parseFloat(details.discount || "0") > 0;

        const colCount =
          3 +
          (hasFixing ? 1 : 0) +
          (hasTransport ? 1 : 0) +
          (hasDiscount ? 1 : 0);

        const gridStyle = {
          gridTemplateColumns: `repeat(${colCount}, minmax(0, 1fr))`,
        };

        return (
          <>
           <div
  className="grid w-full box-border gap-0 bg-blue-600 text-white font-bold text-sm"
  style={gridStyle}
>
              <div className="p-3 text-center border-r border-blue-500">Total</div>
              <div className="p-3 text-center border-r border-blue-500">GST</div>
              {hasFixing && (
                <div className="p-3 text-center border-r border-blue-500">Fixing</div>
              )}
              {hasTransport && (
                <div className="p-3 text-center border-r border-blue-500">Transport</div>
              )}
              {hasDiscount && (
                <div className="p-3 text-center border-r border-blue-500">Discount</div>
              )}
              <div className="p-3 text-center">Total Amount</div>
            </div>

           <div
  className="grid w-full box-border gap-0 bg-blue-50"
  style={gridStyle}
>
              <div className="p-3 text-center border-r border-blue-200 overflow-hidden">
                <p className="text-xs font-bold text-gray-900 break-words">
                  ₹{details.price
                    ? parseFloat(details.price.toString()).toFixed(2)
                    : "0.00"}
                </p>
              </div>
              <div className="p-3 text-center border-r border-blue-200 overflow-hidden">
                <p className="text-xs font-bold text-gray-900 break-words">
                  ₹{details.gst
                    ? parseFloat(details.gst.toString()).toFixed(2)
                    : "0.00"}
                </p>
              </div>
              {hasFixing && (
                <div className="p-3 text-center border-r border-blue-200 overflow-hidden">
                  <p className="text-xs font-bold text-gray-900 break-words">
                    ₹{parseFloat(details.fixing ? details.fixing.toString() : "0").toFixed(2)}
                  </p>
                </div>
              )}
              {hasTransport && (
                <div className="p-3 text-center border-r border-blue-200 overflow-hidden">
                  <p className="text-xs font-bold text-gray-900 break-words">
                    ₹{parseFloat(details.transport ? details.transport.toString() : "0").toFixed(2)}
                  </p>
                </div>
              )}
              {hasDiscount && (
                <div className="p-3 text-center border-r border-blue-200 overflow-hidden">
                  <p className="text-xs font-bold text-gray-900 break-words">
                    ₹{parseFloat(details.discount ? details.discount.toString() : "0").toFixed(2)}
                  </p>
                </div>
              )}
              <div className="p-3 text-center bg-green-100 overflow-hidden">
                <p className="text-xs font-bold text-green-700 break-words">
                  ₹{details.total
                    ? parseFloat(details.total.toString()).toFixed(2)
                    : "0.00"}
                </p>
              </div>
            </div>
          </>
        );
      })()}
    </div>
  </div>
)}

          {details.notes && (
            <div>
              <p className="text-xs text-gray-500 font-semibold mb-2">Notes:</p>
              <div className="bg-gray-50 p-3 rounded-lg border border-gray-200 break-all whitespace-pre-wrap overflow-hidden">
                <p className="text-sm font-bold text-gray-900">{details.notes}</p>
              </div>
            </div>
          )}

          {details.products && details.products.length > 0 && (
            <div className="border-t pt-3">
              <h3 className="text-lg font-bold text-gray-900 mb-4">🛍️ Products</h3>
              <div className="space-y-4">
                {details.products.map((product: any, index: number) => {
                  const isPieces = product.formulaType === "pieces";
                  return (
                  <div key={index} className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-4 space-y-3">
                    <div className="bg-white rounded-lg p-3 border border-green-300">
                      <p className="text-xs text-gray-600 font-bold uppercase mb-2">Product Name</p>
                      <div className="flex items-center gap-3">
                        <AuthMediaImg
                          kind="product"
                          resourceId={product?.product_id || product?.productId}
                          accountId={id}
                          authToken={authToken}
                          inlineSrc={product?.productImage || product?.product_image}
                          hasImage={Boolean(
                            product?.product_id ||
                              product?.productId ||
                              product?.productImage ||
                              product?.hasImage
                          )}
                          alt={product.name}
                          className="w-14 h-14 rounded-lg border border-green-300 object-cover flex-shrink-0 cursor-pointer hover:shadow-md hover:scale-105 transition-all"
                          onClick={(e) => {
                            e.stopPropagation();
                            setPreviewProduct({
                              id: product?.product_id || product?.productId,
                              name: product.name,
                              productImage: product?.productImage || product?.product_image,
                              hasImage: true,
                            });
                          }}
                          fallback={
                            <div className="w-14 h-14 rounded-lg border border-green-300 bg-gray-100 flex items-center justify-center flex-shrink-0 text-lg">
                              📦
                            </div>
                          }
                        />
                        <p className="text-lg font-bold text-green-700 min-w-0 break-words">{product.name}</p>
                      </div>
                    </div>

                    {product.specifications && Object.keys(product.specifications).length > 0 && (
                      <div className="bg-white border-2 border-yellow-300 rounded-lg p-4">
                        <p className="text-xs text-gray-600 font-bold uppercase mb-3">Specifications</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                          {Object.entries(product.specifications).map(([key, value]: [string, any]) => {
                            const colorMeta =
                              isColorSpecKey(key, value) ||
                              (typeof value === "object" && value !== null && (value.image || value.name))
                                ? findColorMeta(value, colorsList)
                                : null;
                            return (
                            <div key={key}>
                              <p className="text-xs text-gray-600 font-bold capitalize mb-1">{key.replace(/_/g, " ")}</p>
                              {colorMeta && (colorMeta.hasImage || colorMeta.name) ? (
                                <div className="flex items-center gap-2 min-w-0">
                                  <AuthMediaImg
                                    kind="color"
                                    resourceId={colorMeta.id}
                                    accountId={id}
                                    authToken={authToken}
                                    inlineSrc={colorMeta.image}
                                    hasImage={colorMeta.hasImage}
                                    alt={colorMeta.name}
                                    className="w-8 h-8 rounded border border-yellow-300 object-cover cursor-pointer hover:shadow-lg hover:scale-110 transition-all shrink-0"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setPreviewColor(colorMeta);
                                    }}
                                    fallback={
                                      <div className="w-8 h-8 rounded border border-yellow-300 bg-gray-200 shrink-0" />
                                    }
                                  />
                                  <p className="text-sm font-bold text-yellow-700 break-words">
                                    {colorMeta.name || "N/A"}
                                  </p>
                                </div>
                              ) : typeof value === "object" && value !== null && value.name ? (
                                <p className="text-sm font-bold text-yellow-700 break-words">{value.name || "N/A"}</p>
                              ) : (
                                <p className="text-sm font-bold text-yellow-700 break-words">{value || "N/A"}</p>
                              )}
                            </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    <div className="bg-white border-2 border-blue-300 rounded-lg p-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-xs text-gray-600 font-bold mb-1">Place</p>
                          <p className="text-sm font-bold text-teal-700 break-words">{product.place || "N/A"}</p>
                        </div>
                        {!isPieces && product.width != null && product.width !== "0" && product.width !== 0 && <div>
                          <p className="text-xs text-gray-600 font-bold mb-1">Width</p>
                          <p className="text-sm font-bold text-blue-700 break-words">
                            {product.width || "N/A"}<sup>{product.nool_width || ""}</sup>
                            {product.width_uom && <span className="text-gray-500 ml-1">{product.width_uom}</span>}
                          </p>
                        </div>}
                      {!isPieces && product.height != null && product.height !== "0" && product.height !== 0 && <div>
                          <p className="text-xs text-gray-600 font-bold mb-1">Height</p>
                          <p className="text-sm font-bold text-cyan-700 break-words">
                            {product.height || "N/A"}<sup>{product.nool_height || ""}</sup>
                            {product.height_uom && <span className="text-gray-500 ml-1">{product.height_uom}</span>}
                          </p>
                        </div>}
                         {!isPieces && product.length != null && product.length !== "0" && product.length !== 0 && <div>
                          <p className="text-xs text-gray-600 font-bold mb-1">Length</p>
                          <p className="text-sm font-bold text-cyan-700 break-words">
                            {product.length || "N/A"}
                            {product.length_uom && <span className="text-gray-500 ml-1">{product.length_uom}</span>}
                          </p>
                        </div>}
                        <div>
                          <p className="text-xs text-gray-600 font-bold mb-1">Quantity</p>
                          <p className="text-sm font-bold text-red-700 break-words">{product.nos || "N/A"}</p>
                        </div>
                        {showMoney && !isPieces && (
                          <div>
                            <p className="text-xs text-gray-600 font-bold mb-1">Area</p>
                            <p className="text-sm font-bold text-indigo-700 break-words">{product.area ? parseFloat(product.area.toString()).toFixed(2) : "N/A"} {product.area_uom || ""}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {showMoney && (
                      <div className="bg-white border-2 border-purple-300 rounded-lg p-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div className="flex flex-col">
                            <p className="text-xs text-gray-600 font-bold mb-1">Price</p>
                            <p className="text-sm font-bold text-pink-700 break-words">₹{product.price ? parseFloat(product.price.toString()).toFixed(2) : "0.00"}</p>
                          </div>
                          <div className="flex flex-col">
                            <p className="text-xs text-gray-600 font-bold mb-1">GST</p>
                            <p className="text-sm font-bold text-orange-700 break-words">{product.gst || "0"}%</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
        </>
          );
        })()}
      </div>

      {showDateModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          onClick={() => !dateLoading && setShowDateModal(false)}
        >
          <div
            className="w-full max-w-sm rounded-xl bg-white p-5 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 className="text-lg font-bold text-gray-900">Edit Invoice Date</h2>
            <p className="mt-1 text-sm text-gray-500">
              Only the date printed on the invoice will be changed.
            </p>
            <label className="mt-4 block text-sm font-semibold text-gray-700">
              Invoice Date
              <input
                type="date"
                required
                value={invoiceDate}
                disabled={dateLoading}
                onChange={(event) => {
                  setInvoiceDate(event.target.value);
                  setDateError("");
                }}
                className="mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-200"
              />
            </label>
            {dateError && <p className="mt-2 text-sm font-medium text-red-600">{dateError}</p>}
            <div className="mt-5 flex justify-end gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={dateLoading}
                onClick={() => setShowDateModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                disabled={dateLoading || !invoiceDate}
                onClick={handleInvoiceDateSave}
                className="bg-orange-600 text-white hover:bg-orange-700"
              >
                {dateLoading ? "Saving..." : "Save"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Color Image Preview */}
      {previewColor && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-[70] flex items-center justify-center p-4" onClick={() => setPreviewColor(null)}>
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-in zoom-in-95 duration-300 flex flex-col items-center gap-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setPreviewColor(null)}
              className="absolute top-4 right-4 flex items-center justify-center w-8 h-8 rounded-full bg-pink-100 hover:bg-pink-200 text-pink-600 font-bold text-lg transition-all hover:scale-110"
              title="Close"
            >
              ✕
            </button>
            <div className="w-full aspect-square rounded-xl overflow-hidden border-4 border-pink-300 shadow-md flex items-center justify-center bg-gray-50">
              <AuthMediaImg
                kind="color"
                resourceId={previewColor.id}
                accountId={id}
                authToken={authToken}
                inlineSrc={previewColor.image || previewColor.colorImage}
                hasImage={Boolean(
                  previewColor.hasImage ||
                    previewColor.id ||
                    previewColor.image ||
                    previewColor.colorImage
                )}
                alt={previewColor.name}
                className="w-full h-full object-cover"
                fallback={
                  <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                    <span className="text-gray-500 text-sm">No image available</span>
                  </div>
                }
              />
            </div>
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-800">{previewColor.name}</h3>
              <p className="text-sm text-gray-500 mt-1">Color</p>
            </div>
          </div>
        </div>
      )}

      {/* Product Image Preview */}
      {previewProduct && (
        <div
          className="fixed inset-0 bg-black bg-opacity-60 z-[70] flex items-center justify-center p-4"
          onClick={() => setPreviewProduct(null)}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 animate-in zoom-in-95 duration-300 flex flex-col items-center gap-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setPreviewProduct(null)}
              className="absolute top-4 right-4 flex items-center justify-center w-8 h-8 rounded-full bg-emerald-100 hover:bg-emerald-200 text-emerald-700 font-bold text-lg transition-all hover:scale-110"
              title="Close"
            >
              ✕
            </button>
            <div className="w-full aspect-square rounded-xl overflow-hidden border-4 border-emerald-300 shadow-md flex items-center justify-center bg-gray-50">
              <AuthMediaImg
                kind="product"
                resourceId={previewProduct.id}
                accountId={id}
                authToken={authToken}
                inlineSrc={previewProduct.productImage}
                hasImage={Boolean(
                  previewProduct.hasImage || previewProduct.id || previewProduct.productImage
                )}
                alt={previewProduct.name}
                className="w-full h-full object-cover"
                fallback={
                  <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                    <span className="text-gray-500 text-sm">No image available</span>
                  </div>
                }
              />
            </div>
            <div className="text-center">
              <h3 className="text-xl font-bold text-gray-800">{previewProduct.name}</h3>
              <p className="text-sm text-gray-500 mt-1">Product</p>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};
